import React, { useMemo, useRef, useEffect, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Empty, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

const AffinityGraph = ({ data }) => {
  const fgRef = useRef();
  // Initialize with window width approximation to avoid "half-screen" fallback
  const [dimensions, setDimensions] = useState({ 
    width: window.innerWidth - 300, // Approximate sidebar/padding subtraction
    height: 600 
  });
  const [hoveredNode, setHoveredNode] = useState(null);
  const [targetNode, setTargetNode] = useState(null);
  const containerRef = useRef(null);

  // Resize handling with ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        if (width > 0) {
          setDimensions({ width, height: height || 600 });
        }
      }
    };

    // Initial check
    updateDimensions();

    // Double check after a short delay (for Tabs/Layout animations)
    const timer = setTimeout(updateDimensions, 200);

    const observeTarget = containerRef.current;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.target.getBoundingClientRect();
        if (width > 0) {
          setDimensions({ width, height: height || 600 });
        }
      }
    });

    resizeObserver.observe(observeTarget);
    
    return () => {
      resizeObserver.unobserve(observeTarget);
      clearTimeout(timer);
    };
  }, []);

  const graphData = useMemo(() => {
    if (!data || data.length === 0) return { nodes: [], links: [] };

    const nodesMap = new Map();
    const links = [];
    const processedPairs = new Set();

    data.forEach(item => {
      // Create a unique key for the pair to deduplicate A->B and B->A
      const pairKey = [item.product_name, item.related_product_name].sort().join('||');
      
      if (processedPairs.has(pairKey)) return;
      processedPairs.add(pairKey);

      // 還原該商品的總出貨量 (total = score / strength)
      const derivedVolumeA = item.score / (item.strength / 100 || 1);
      
      // Process Node A
      if (!nodesMap.has(item.product_name)) {
        nodesMap.set(item.product_name, { id: item.product_name, volume: derivedVolumeA });
      }

      // Process Node B
      if (!nodesMap.has(item.related_product_name)) {
        nodesMap.set(item.related_product_name, { id: item.related_product_name, volume: item.score });
      }

      // Add Link
      links.push({
        source: item.product_name,
        target: item.related_product_name,
        value: item.strength,
        score: item.score
      });
    });

    // Finalize Volumes and Calculate Radius
    const nodes = Array.from(nodesMap.values());
    const volumes = nodes.map(n => n.volume);
    const minVolume = Math.min(...volumes);
    const maxVolume = Math.max(...volumes);

    const logMin = Math.log2(minVolume || 1);
    const logMax = Math.log2(maxVolume || 1);
    const scaleRange = logMax - logMin;

    const MIN_RADIUS = 6;
    const MAX_RADIUS = 40;

    nodes.forEach(node => {
      const logVal = Math.log2(node.volume || 1);
      const ratio = scaleRange > 0 ? (logVal - logMin) / scaleRange : 0.5;
      node.radius = MIN_RADIUS + (ratio * (MAX_RADIUS - MIN_RADIUS));
    });

    // Relative edge length: high-affinity pairs pulled closer, weak pairs pushed apart.
    // Normalize across all links so the layout spreads across MIN_DISTANCE~MAX_DISTANCE
    // regardless of the absolute affinity range (avoids "all bunched" or "all far").
    const MIN_DISTANCE = 40;
    const MAX_DISTANCE = 260;
    const linkValues = links.map(l => l.value);
    const minVal = linkValues.length ? Math.min(...linkValues) : 0;
    const maxVal = linkValues.length ? Math.max(...linkValues) : 0;
    const valRange = maxVal - minVal;

    links.forEach(link => {
      const ratio = valRange > 0 ? (link.value - minVal) / valRange : 0.5;
      link.distance = MAX_DISTANCE - ratio * (MAX_DISTANCE - MIN_DISTANCE);
      // Stronger pull on high-affinity links so they "win" over weaker competing links
      link.forceStrength = 0.2 + ratio * 0.6;
    });

    return { nodes, links };
  }, [data]);

  // Configure Forces
  useEffect(() => {
    if (fgRef.current) {
      fgRef.current.d3Force('charge').strength(-400);
      fgRef.current.d3Force('link')
        .distance(link => link.distance ?? 180)
        .strength(link => link.forceStrength ?? 0.3);
      fgRef.current.d3Force('center').strength(0.5);
    }
  }, [graphData, dimensions.width]);

  const handleSelect = (value) => {
    if (!value) {
      setTargetNode(null);
      return;
    }
    
    const node = graphData.nodes.find(n => n.id === value);
    
    if (node) {
      setTargetNode(node);
      // Center and Zoom
      fgRef.current.centerAt(node.x, node.y, 1000);
      fgRef.current.zoom(2.5, 2000); // Zoom level 2.5
    } else {
      setTargetNode(null);
    }
  };

  // Prepare options for Select
  const searchOptions = useMemo(() => {
    return graphData.nodes.map(n => ({ value: n.id, label: n.id }));
  }, [graphData]);

  if (!data || data.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <div ref={containerRef} style={{ width: '100%', height: '600px', border: '1px solid #f0f0f0', borderRadius: '8px', overflow: 'hidden', background: '#fafafa', position: 'relative' }}>
      
      {/* Search Bar Overlay */}
      <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 10, width: 300 }}>
        <Select
          showSearch
          allowClear
          style={{ width: '100%' }}
          placeholder="Search Product..."
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={handleSelect}
          options={searchOptions}
          suffixIcon={<SearchOutlined />}
        />
      </div>

      <ForceGraph2D
        ref={fgRef}
        width={dimensions.width}
        height={dimensions.height}
        graphData={graphData}
        
        // Interaction
        enableNodeDrag={false}
        onNodeHover={node => setHoveredNode(node)}
        
        // Tooltips
        nodeLabel={node => `
          <div style="color: #fff; background: rgba(0,0,0,0.8); padding: 8px; border-radius: 4px; font-size: 12px;">
            <div style="font-weight: bold; margin-bottom: 4px;">${node.id}</div>
            <div>Total Volume: ${Math.round(node.volume)}</div>
          </div>
        `}
        linkLabel={link => {
          if (hoveredNode) return null; // Suppress link tooltip if node is hovered
          return `
            <div style="color: #fff; background: rgba(0,0,0,0.8); padding: 8px; border-radius: 4px; font-size: 12px;">
              <div style="font-weight: bold; margin-bottom: 4px;">Relationship</div>
              <div>${link.source.id} + ${link.target.id}</div>
              <div style="margin-top: 4px; border-top: 1px solid #555; padding-top: 4px;">
                Co-picks: ${link.score}<br/>
                Affinity: ${link.value.toFixed(1)}%
              </div>
            </div>
          `;
        }}
        
        // Static Layout Behavior
        cooldownTicks={150}
        onEngineStop={() => fgRef.current.zoomToFit(400)}
        
        // Custom Node Rendering
        nodeCanvasObject={(node, ctx, globalScale) => {
          const fullLabel = node.id;
          const displayLabel = fullLabel.length > 5 ? `${fullLabel.substring(0, 5)}...` : fullLabel;
          const fontSize = 14 / globalScale;
          ctx.font = `${fontSize}px Sans-Serif`;
          const radius = node.radius || 6;
          
          const isTarget = targetNode && node.id === targetNode.id;
          const isHovered = node === hoveredNode;

          // Draw Node Circle
          ctx.beginPath();
          ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
          
          if (isTarget) {
            ctx.fillStyle = '#ff4d4f'; // Red for searched node
            ctx.shadowColor = '#ff4d4f';
            ctx.shadowBlur = 15;
          } else {
            ctx.fillStyle = isHovered ? '#1890ff' : 'rgba(24, 144, 255, 0.8)';
            ctx.shadowBlur = 0;
          }
          
          ctx.fill();
          
          // Reset shadow for text
          ctx.shadowBlur = 0;

          // Draw Text
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = '#333';
          ctx.fillText(displayLabel, node.x, node.y + radius + (4 / globalScale));
        }}
        
        // Expand click/hover area
        nodePointerAreaPaint={(node, color, ctx) => {
          ctx.fillStyle = color;
          const radius = node.radius || 6;
          ctx.beginPath();
          ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
          ctx.fill();
        }}

        // Link styling
        linkWidth={link => 1 + (link.value / 100) * 2} 
        linkColor={link => {
          const ratio = link.value / 100;
          const opacity = Math.max(0.1, Math.pow(ratio, 2)); 
          return `rgba(24, 144, 255, ${opacity})`; 
        }}
        linkCurvature={0.1}
      />
    </div>
  );
};

export default AffinityGraph;
